





















































import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
} from "@vue/composition-api";
import {
  eventGetters,
  supplierGetters,
  useProduct,
} from "@konfetti/composables";
import { EventDateType } from "@konfetti/composables/src";
import { useKftBuyBox } from "../e/_event/composables/useKftBuyBox";
import cacheControl from "~/helpers/cacheControl";
import { useBooking } from "~/composables";
import { KftBuyBox } from "~/components";

export default {
  name: "EmbeddedBooking",

  components: {
    KftBuyBox,
  },

  layout: "blank",

  middleware: cacheControl({
    disableCache: true,
  }),

  setup(props, { root: { $i18n, $route, $config } }) {
    /** VARIABLES
     * -------------------------- */
    const kftBuyBoxPublicRef = ref(null);
    const kftBuyBoxPrivateRef = ref(null);
    const pollingIntervalId = ref(null);
    const eventDescriptionId = ref(null);
    const settedUp = ref(false);
    const backgroundColor = ref("#000000");
    const textColor = ref("#FFFFFF");
    const {
      /* Variables */
      event,
      error,
      getEventById,
    } = useProduct("embedded-checkout");

    const {
      /* Methods */
      getEventPrice,
    } = useBooking("event-page");

    const supplierHasEmbeddedGifcard = computed(() =>
      supplierGetters.getHasGiftcardEmbedded(
        eventGetters.getSupplier(event.value.data),
      ),
    );

    const {
      publicEventDateList,
      loadingPublicEventDates,
      publicEventDatePagination,
      privateEventDateList,
      loadingPrivateEventDates,
      privateEventDatePagination,
      handleReachedEndOfPublicDatesList,
      handleReachedEndOfPrivateDatesList,
      fetchEventDates,
    } = useKftBuyBox(
      `embedded-checkout-${$route.query?.eventDescriptionId}`,
      event,
    );

    /** COMPUTED
     * -------------------------- */
    const price = computed(() => {
      if (!process.client || !event?.value) {
        return "-";
      }

      return getEventPrice(event.value);
    });

    /** METHODS
     * -------------------------- */

    /** @name book
     ** @desc Setup an event (public or private) for booking and redirects to the checkout
     **/
    const book = (params) => {
      const q = Object.keys($route.query ?? {})
        .map((key) => `${key}=${$route.query[key]}`)
        .join("&");

      window.open(
        `${$config.baseUrl}/de-de/checkout/?private=${
          params.private
        }&embedded=true&type=event&eventId=${params.id}&quantity=${
          params.quantity
        }&${q}&supplierId=${eventGetters.getSupplierId(event.value.data)}`,
      );
    };

    /** @name gift
     ** @desc Setup giftcards and redirect to the checkout
     **/
    const gift = (params) => {
      const q = Object.keys($route.query ?? {})
        .map((key) => `${key}=${$route.query[key]}`)
        .join("&");

      const giftcardSelectedTimeId = params.id ? `&eventId=${params.id}` : "";

      window.open(
        `${
          $config.baseUrl
        }/de-de/checkout/?embedded=true&type=giftcard${giftcardSelectedTimeId}&quantity=${
          params.quantity
        }&${q}&supplierId=${eventGetters.getSupplierId(event.value.data)}`,
      );
    };

    const selectDates = async () => {
      await kftBuyBoxPublicRef.value?.setNextAvailableDate();
      await kftBuyBoxPrivateRef.value?.setNextAvailableDate({
        shouldSetTicketsQuantity: false,
      });
    };

    const fnRegisterPollingForUpdatingEventDates = () => {
      // Registers polling function for updating event availability
      pollingIntervalId.value = setInterval(async () => {
        await fetchEventDates();
        await selectDates();
      }, 120 * 1000);
    };

    /** HOOKS
     * -------------------------- */
    const onScriptReady = async () => {
      await getEventById(eventDescriptionId.value);
      await fetchEventDates(true);
      await selectDates(true);
      fnRegisterPollingForUpdatingEventDates();

      settedUp.value = true;
    };

    onMounted(async () => {
      eventDescriptionId.value = $route.query.eventDescriptionId || null;
      backgroundColor.value = $route.query.backgroundColor || "000000";
      textColor.value = $route.query.textColor || "FFFFFF";

      if (eventDescriptionId.value === null) {
        return;
      }

      await onScriptReady();
    });

    onBeforeUnmount(() => {
      // Stops polling function for updating event availability
      clearInterval(pollingIntervalId.value);
    });

    return {
      /* Variables */
      kftBuyBoxPublicRef,
      kftBuyBoxPrivateRef,
      locale: $i18n.locale,
      error,
      event,
      price,
      backgroundColor,
      textColor,
      eventGetters,
      settedUp,
      supplierHasEmbeddedGifcard,
      publicEventDateList,
      publicEventDatePagination,
      loadingPublicEventDates,
      privateEventDateList,
      privateEventDatePagination,
      loadingPrivateEventDates,
      EventDateType,

      /* Methods */
      book,
      gift,
      handleReachedEndOfPublicDatesList,
      handleReachedEndOfPrivateDatesList,
    };
  },
};
